import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import pick from 'lodash/pick';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

import { ESearchFilters } from '@/constants/searchFilters';
import useQueryParams from '@/hooks/useQueryParams';
import { setHeaderSearchFilter } from '@/redux/modules/globalHeader';
import { applySearchFilter } from '@/redux/modules/search';
import type { IRenterSearchedInfo } from '@/services/analytics/search/types';
import { EDeliveryOption } from '@/services/types/core/delivery.types';

import { trackGuidedSearchStepAnswers, trackRenterSearched } from './analytics';
import { buildGuidedSearchParams } from './buildGuidedSearchParams';
import {
  EGuidedSearchComponentType,
  IDeliveryInfo,
  ITripDistanceInfo,
  IVehicleInfo,
  TGuidedSearchComponent,
  TGuidedSearchResponse,
} from './types';

const singleSelectionComponents = [EGuidedSearchComponentType.ButtonGroupRadio];

type TStepsSelection = Record<string, Record<string, string | string[] | undefined>>;

type TGuidedSearchContext = {
  reset: () => void;
  goToPrevStep?: () => void;
  goToNextStep?: (selectionOverride?: TStepsSelection) => void;
  goToStep: (step: number) => void;
  applySelection: () => void;
  trackCurrentStepAnswered: (selectionOverride?: TStepsSelection) => void;
  source: IRenterSearchedInfo['source'];
  currentStep: number;
  numberOfSteps: number;
  currentStepComponent?: TGuidedSearchComponent;
  currentStepSelection?: Record<string, string | string[] | undefined>;
  updateCurrentStepSelection: (fieldId: string, value?: string | string[]) => void;
  hasSelectedData?: boolean;
  hasTowSelected: boolean;
  hasDeliverySelected: boolean;
  hasDriveSelected: boolean;
  stepsSelection: TStepsSelection;
  deliveryInfo: IDeliveryInfo;
  vehicleInfo: IVehicleInfo;
  tripDistanceInfo: ITripDistanceInfo;
  updateDeliveryInfo: (info: IDeliveryInfo) => void;
  updateVehicleInfo: (info: IVehicleInfo) => void;
  updateTripDistanceInfo: (info: ITripDistanceInfo) => void;
  deliveryInfoRef: React.MutableRefObject<IDeliveryInfo>;
  vehicleInfoRef: React.MutableRefObject<IVehicleInfo>;
  tripDistanceInfoRef: React.MutableRefObject<ITripDistanceInfo>;
};

const GuidedSearchContext = createContext<TGuidedSearchContext>({
  reset: noop,
  goToStep: noop,
  applySelection: noop,
  trackCurrentStepAnswered: noop,
  source: 'guided_search_1',
  currentStep: 1,
  numberOfSteps: 1,
  updateCurrentStepSelection: noop,
  hasTowSelected: false,
  hasDeliverySelected: false,
  hasDriveSelected: false,
  stepsSelection: {},
  deliveryInfo: {},
  vehicleInfo: {},
  tripDistanceInfo: {},
  updateDeliveryInfo: noop,
  updateVehicleInfo: noop,
  updateTripDistanceInfo: noop,
  deliveryInfoRef: { current: {} },
  vehicleInfoRef: { current: {} },
  tripDistanceInfoRef: { current: {} },
});

export const useGuidedSearchContext = () => useContext(GuidedSearchContext);

export const GuidedSearchProvider: React.FC<
  React.PropsWithChildren<{
    onApplySelection?: () => void;
    source?: IRenterSearchedInfo['source'];
    guidedSearchData?: TGuidedSearchResponse;
  }>
> = ({ onApplySelection, source = 'guided_search_1', guidedSearchData, children }) => {
  const dispatch = useDispatch();
  const queryParams = useQueryParams();

  // Filter out the components that contains non recognized component types
  const guidedSearchStepsData = useMemo(() => {
    return guidedSearchData?.data_file.components.reduce((acc, component) => {
      const fields = component.fields.filter(field =>
        Object.values(EGuidedSearchComponentType).includes(field.component_type),
      );

      if (!fields.length) {
        return acc;
      }

      return [...acc, { ...component, fields }];
    }, [] as TGuidedSearchComponent[]);
  }, [guidedSearchData]);

  const numberOfSteps = guidedSearchStepsData?.length || 0;

  const [currentStep, setCurrentStep] = useState(1);
  const [stepsSelection, setStepsSelection] = useState<TStepsSelection>({});
  const [hasTowSelected, setHasTowSelected] = useState(false);
  const [hasDeliverySelected, setHasDeliverySelected] = useState(false);
  const [hasDriveSelected, setHasDriveSelected] = useState(false);
  const [deliveryInfo, setDeliveryInfo] = useState<IDeliveryInfo>({});
  const [vehicleInfo, setVehicleInfo] = useState<IVehicleInfo>({});
  const [tripDistanceInfo, setTripDistanceInfo] = useState<ITripDistanceInfo>({});

  // Refs to avoid stale closures in applySelection (called via setTimeout in vehicle hook)
  const deliveryInfoRef = useRef<IDeliveryInfo>({});
  const vehicleInfoRef = useRef<IVehicleInfo>({});
  const tripDistanceInfoRef = useRef<ITripDistanceInfo>({});

  const updateDeliveryInfo = useCallback((info: IDeliveryInfo) => {
    setDeliveryInfo(info);
    deliveryInfoRef.current = info;
  }, []);

  const updateVehicleInfo = useCallback((info: IVehicleInfo) => {
    setVehicleInfo(info);
    vehicleInfoRef.current = info;
  }, []);

  const updateTripDistanceInfo = useCallback((info: ITripDistanceInfo) => {
    setTripDistanceInfo(info);
    tripDistanceInfoRef.current = info;
  }, []);

  const reset = () => {
    setCurrentStep(1);
    setHasTowSelected(false);
    setHasDeliverySelected(false);
    setHasDriveSelected(false);
  };

  const goToStep = (step: number) => {
    if (step >= 1 && step <= numberOfSteps) {
      // If we're moving away from the last step (page-4), reset special selections
      if (currentStep === numberOfSteps && step !== numberOfSteps) {
        setHasTowSelected(false);
        setHasDeliverySelected(false);
        setHasDriveSelected(false);
      }
      setCurrentStep(step);
    }
  };

  const goToPrevStep =
    currentStep > 1
      ? () => {
          goToStep(currentStep - 1);
        }
      : undefined;

  const trackCurrentStepAnswered = (selectionOverride?: TStepsSelection) => {
    const step = guidedSearchStepsData?.[currentStep - 1];
    if (!step) return;
    trackGuidedSearchStepAnswers(
      step,
      currentStep,
      (selectionOverride || stepsSelection)[step.id],
      source,
    );
  };

  const goToNextStep =
    currentStep < numberOfSteps
      ? (selectionOverride?: TStepsSelection) => {
          trackCurrentStepAnswered(selectionOverride);
          setCurrentStep(currentStep + 1);
        }
      : undefined;

  const applySelection = async (currentStepsSelection?: TStepsSelection) => {
    const selection = currentStepsSelection || stepsSelection;

    if (!guidedSearchStepsData || isEmpty(selection)) return;

    // Build the query params from the selection (radio rows + the location/dates/
    // guests fields). Shared with InlineGuidedSearch so the auto-advance path here
    // and the locality redirect path stay in sync.
    const params = buildGuidedSearchParams(guidedSearchStepsData, selection);

    // Closing the modal early also lands here; only the last step counts as answered.
    if (currentStep === numberOfSteps) {
      trackCurrentStepAnswered(selection);
    }

    // Add delivery params from context state if available
    const currentDeliveryInfo = deliveryInfoRef.current;
    if (params['delivery']) {
      if (currentDeliveryInfo.deliveryStationary) {
        params['deliveryStationary'] = currentDeliveryInfo.deliveryStationary;
      } else {
        params['deliveryStationary'] = EDeliveryOption.MOVING;
      }
      const fullDeliveryAddress =
        currentDeliveryInfo.deliveryDetails || currentDeliveryInfo.address;
      if (fullDeliveryAddress) {
        params['deliveryAddress'] = fullDeliveryAddress;
      }
      if (currentDeliveryInfo.stationaryAcknowledged) {
        params['stationary_acknowledged'] = 'true';
      }
      if (currentDeliveryInfo.deliveryCenter) {
        params['deliveryCenter'] = currentDeliveryInfo.deliveryCenter;
      }
      if (currentDeliveryInfo.deliveryDetails) {
        params['deliveryDetails'] = currentDeliveryInfo.deliveryDetails;
      }
      if (currentDeliveryInfo.mapboxId) {
        params['mapbox_id'] = currentDeliveryInfo.mapboxId;
      }
      if (fullDeliveryAddress) {
        params[ESearchFilters.ADDRESS] = fullDeliveryAddress;
      }
    }

    // Add towing vehicle params if tow was selected
    const currentVehicleInfo = vehicleInfoRef.current;
    if (hasTowSelected) {
      if (currentVehicleInfo.towingCapacity) {
        params['trailerWeight[lte]'] = String(currentVehicleInfo.towingCapacity);
      }
      if (currentVehicleInfo.modelId) {
        params['towing_id'] = String(currentVehicleInfo.modelId);
      }
      if (currentVehicleInfo.year) {
        params['towable_year'] = String(currentVehicleInfo.year);
      }
      if (currentVehicleInfo.make) {
        params['towable_make'] = currentVehicleInfo.make;
      }
    }

    const currentTripDistanceInfo = tripDistanceInfoRef.current;
    if (hasDriveSelected) {
      if (currentTripDistanceInfo.estimatedTripDistance) {
        params[ESearchFilters.ESTIMATED_TRIP_DISTANCE] = String(
          currentTripDistanceInfo.estimatedTripDistance,
        );
      }
    }

    trackRenterSearched(params, {
      source,
    });

    // Update the header search bar address if delivery address is present
    if (params[ESearchFilters.ADDRESS]) {
      dispatch(setHeaderSearchFilter({ address: params[ESearchFilters.ADDRESS] }));
    }

    dispatch(
      applySearchFilter(
        {
          // Persist location & date selection
          ...pick(queryParams, [
            ESearchFilters.ADDRESS,
            ESearchFilters.BOUNDS_NE,
            ESearchFilters.BOUNDS_SW,
            ESearchFilters.DATE_FROM,
            ESearchFilters.DATE_TO,
          ]),
          ...params,
          [ESearchFilters.SKIP_DEFAULTS]: 'true',
        },

        false,
        false,
        false,
        undefined,
        true,
      ),
    );

    onApplySelection?.();
  };

  const currentStepComponent = guidedSearchStepsData?.[currentStep - 1];

  // Update hasTowSelected, hasDeliverySelected, and hasDriveSelected based on current step and selection
  useEffect(() => {
    if (currentStep === numberOfSteps && currentStepComponent?.id === 'page-4') {
      const selectedValue = stepsSelection['page-4']?.['tow-drive-delivery'];

      setHasTowSelected(selectedValue === '1');
      setHasDriveSelected(selectedValue === '2');

      // Check if the selected option has a delivery query_param
      const field = currentStepComponent.fields.find(f => f.id === 'tow-drive-delivery');
      const selectedRow = field?.properties.rows?.find(row => row.id === selectedValue);
      const isDelivery = !!selectedRow?.query_params.some(param => param.key === 'delivery');
      setHasDeliverySelected(isDelivery);
    } else {
      setHasTowSelected(false);
      setHasDeliverySelected(false);
      setHasDriveSelected(false);
    }
  }, [currentStep, numberOfSteps, currentStepComponent, stepsSelection]);

  const moveToNextStepAfterSelection = useMemo(() => {
    if (!currentStepComponent) return false;

    return (
      currentStepComponent.fields.length === 1 &&
      currentStepComponent.fields.every(field =>
        singleSelectionComponents.includes(field.component_type),
      )
    );
  }, [currentStepComponent]);

  // Check if the selection is for "Tow" on page-4
  const checkIfTowSelected = (fieldId: string, value?: string | string[]) => {
    if (currentStepComponent?.id !== 'page-4') {
      return false;
    }

    // Simple check: field is 'tow-drive-delivery' and value is '1' (Tow option)
    return fieldId === 'tow-drive-delivery' && value === '1';
  };

  // Check if the selection is for "Drive" on page-4
  const checkIfDriveSelected = (fieldId: string, value?: string | string[]) => {
    if (currentStepComponent?.id !== 'page-4') {
      return false;
    }

    // Simple check: field is 'tow-drive-delivery' and value is '2' (Drive option)
    return fieldId === 'tow-drive-delivery' && value === '2';
  };

  // Check if the selection is for "Delivery" on page-4 by checking query_params
  const checkIfDeliverySelected = (fieldId: string, value?: string | string[]) => {
    if (currentStepComponent?.id !== 'page-4' || fieldId !== 'tow-drive-delivery') {
      return false;
    }

    const field = currentStepComponent.fields.find(f => f.id === fieldId);
    if (!field) return false;

    const selectedRow = field.properties.rows?.find(row => row.id === value);
    return !!selectedRow?.query_params.some(param => param.key === 'delivery');
  };

  const currentStepSelection = stepsSelection[currentStepComponent?.id || ''];
  const updateCurrentStepSelection = (fieldId: string, value?: string | string[]) => {
    if (!currentStepComponent) return;

    const nextStepsSelection: TStepsSelection = {
      ...stepsSelection,
      [currentStepComponent.id]: {
        ...stepsSelection[currentStepComponent.id],
        [fieldId]: value,
      },
    };

    setStepsSelection(nextStepsSelection);

    // Check if Tow, Drive, or Delivery is selected
    const isTowSelected = checkIfTowSelected(fieldId, value);
    const isDriveSelected = checkIfDriveSelected(fieldId, value);
    const isDeliverySelected = checkIfDeliverySelected(fieldId, value);
    setHasTowSelected(isTowSelected);
    setHasDriveSelected(isDriveSelected);
    setHasDeliverySelected(isDeliverySelected);

    // Auto-advance logic: advance to next step UNLESS Tow, Delivery, or Drive is selected.
    if (moveToNextStepAfterSelection && !isTowSelected && !isDriveSelected && !isDeliverySelected) {
      (goToNextStep || applySelection)(nextStepsSelection);
    }
  };

  const hasSelectedData = !!(stepsSelection && Object.keys(stepsSelection).length);

  if (!guidedSearchStepsData?.length) return null;

  return (
    <GuidedSearchContext.Provider
      value={{
        reset,
        goToPrevStep,
        goToNextStep,
        goToStep,
        applySelection,
        trackCurrentStepAnswered,
        source,
        currentStep,
        numberOfSteps,
        currentStepComponent,
        currentStepSelection,
        updateCurrentStepSelection,
        hasSelectedData,
        hasTowSelected,
        hasDeliverySelected,
        hasDriveSelected,
        stepsSelection,
        deliveryInfo,
        vehicleInfo,
        tripDistanceInfo,
        updateDeliveryInfo,
        updateVehicleInfo,
        updateTripDistanceInfo,
        deliveryInfoRef,
        vehicleInfoRef,
        tripDistanceInfoRef,
      }}>
      {children}
    </GuidedSearchContext.Provider>
  );
};
